// TS twin of iOS `ATOLog.swift`.
//
// Always console-mirrors locally; when a PostHog client is wired via
// `configure({client})` (typically at main.ts startup), structured events
// also fire to the analytics layer. The dev-console emojis match the iOS
// `print("[ATO] 📡 …")` prefix so the same grep pattern works in
// Console.app (iOS) and browser DevTools (Android).
//
// Event names + property keys MUST match iOS exactly so PostHog dashboards
// can union both platforms by `platform`. See dual-codebase-strategy.md §11.

export interface ATOLogClient {
  capture(name: string, properties?: Record<string, unknown>): void
  register(properties: Record<string, unknown>): void
}

interface ConfigureOptions {
  client?: ATOLogClient | null
  consoleMirror?: boolean
}

let _client: ATOLogClient | null = null
let _consoleMirror = true

function event(name: string, properties?: Record<string, unknown>): void {
  if (_consoleMirror) console.log(`[ATO] 📡 ${name}`, properties ?? '')
  _client?.capture(name, properties)
}

function debug(message: string): void {
  if (_consoleMirror) console.log(`[ATO] ${message}`)
}

/**
 * Reports a CAUGHT error as its own event.
 *
 * A handled error is invisible by construction: the catch block turns
 * it into a toast, a fallback, or a refusal reason, and nothing reaches
 * telemetry. That is precisely how a device that cannot start AR at all
 * ends up reporting nothing — the failure is caught and rendered as an
 * on-screen message (2026-08-24).
 *
 * One event name with an `error_scope` rather than bespoke events per
 * call site: the scope is a PostHog breakdown, and adding a new call
 * site costs one argument instead of a new dashboard.
 *
 * Never throws. Every call site is already inside a catch, and
 * telemetry that can fail there would turn a handled error into an
 * unhandled one.
 */
function error(
  scope: string,
  err: unknown,
  extra?: Record<string, unknown>,
): void {
  try {
    event('ato_error', {
      ...errorProperties(err),
      ...extra,
      // Last, so caller context can never overwrite the scope the call
      // site declared.
      error_scope: scope,
    })
  } catch {
    // Deliberately silent: there is nowhere left to report to.
  }
}

function register(properties: Record<string, unknown>): void {
  if (_consoleMirror) console.log('[ATO] 🔖 register', properties)
  _client?.register(properties)
}

function configure(opts: ConfigureOptions): void {
  if (opts.client !== undefined) _client = opts.client
  if (opts.consoleMirror !== undefined) _consoleMirror = opts.consoleMirror
}

function errorProperties(error: unknown): Record<string, unknown> {
  if (!(error instanceof Error)) {
    return { error_domain: 'unknown', error_description: String(error) }
  }
  const props: Record<string, unknown> = {
    error_domain: error.name,
    error_description: error.message,
  }
  const code = (error as Error & { code?: unknown }).code
  if (typeof code === 'string' || typeof code === 'number') {
    props.error_code = code
  }
  const cause = (error as Error & { cause?: unknown }).cause
  if (cause instanceof Error) {
    props.underlying_domain = cause.name
    const underlyingCode = (cause as Error & { code?: unknown }).code
    if (typeof underlyingCode === 'string' || typeof underlyingCode === 'number') {
      props.underlying_code = underlyingCode
    }
  }
  return props
}

export const atoLog = {
  configure,
  event,
  debug,
  error,
  register,
  errorProperties,
}
